import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Canvas from './Canvas';
import { ConnectionStatusBanner } from './ConnectionStatusBanner';
import { useBookingExtraction } from './useBookingExtraction';
import { useAutoSave, useDraftManager, useCaseManager, useEmailFlow } from './hooks';
import {
  useClaimState,
  clearDraftFromStorage,
} from './useClaimState';
import type { FlightSegment, Expense, IncidentType, UploadedDocument, ExtractedBookingData } from './types';
import type { ClaimTheme } from './constants';
import { apiEndpoints } from './constants';
import api from '@/utils/api';
import { AuthThemeToggle } from '@/components/auth/ThemeToggle';

interface NewClaimPageProps {
  theme?: ClaimTheme;
  onThemeChange?: (theme: ClaimTheme) => void;
}

export default function NewClaimPage({ theme = 'invol', onThemeChange }: NewClaimPageProps) {
  const navigate = useNavigate();

  // Get resumeCase from URL search params
  const search = useSearch({ strict: false }) as { resumeCase?: string };
  const resumeCaseId = search?.resumeCase;

  // Booking extraction hook (WebSocket)
  const {
    extractBooking,
    isExtracting,
    progress: extractionProgress,
    extractedData,
    error: extractionError,
    reset: resetExtraction,
    wsStatus,
    reconnectAttempt,
    retryConnection,
  } = useBookingExtraction();

  // Main claim state
  const {
    state,
    dispatch,
    goToPhase,
    goBack,
    goNext,
    setExtractedData,
    updateSegment,
    deleteSegment,
    addSegment,
    updatePassenger,
    deletePassenger,
    addPassenger,
    setIncidentType,
    setAffectedSegment,
    setIncidentDescription,
    addDocument,
    updateDocument,
    removeDocument,
    addExpense,
    removeExpense,
    setNarrative,
    setCaseId,
    setError,
    markSaved,
    reset,
    visibleSegments,
  } = useClaimState();

  // Draft manager - loads drafts from localStorage/database
  const { hasInitialized, hasLoadedDraft } = useDraftManager({
    resumeCaseId,
    state,
    dispatch,
    setCaseId,
    goToPhase,
    navigate,
  });

  // Email flow - handles pending tokens and email notifications
  const { emailNotification } = useEmailFlow({
    state,
    dispatch,
    goToPhase,
    setExtractedData,
    resumeCaseId,
    navigate,
    hasLoadedDraft,
  });

  // Case manager - auto-creates case when needed
  const { isCreatingCase } = useCaseManager({
    state,
    setCaseId,
  });

  // Auto-save hook - saves to localStorage and database
  useAutoSave(state);

  // Sync extraction data to state
  useEffect(() => {
    if (extractedData && !hasInitialized.current) {
      setExtractedData(extractedData);
      goToPhase('booking_review');
      hasInitialized.current = true;
    }
  }, [extractedData, setExtractedData, goToPhase]);

  // Handle extraction state
  useEffect(() => {
    if (isExtracting && state.phase === 'welcome') {
      goToPhase('extracting');
    }
  }, [isExtracting, state.phase, goToPhase]);

  // Handle extraction error
  useEffect(() => {
    if (extractionError) {
      setError(extractionError);
      goToPhase('welcome');
      toast.error(extractionError);
    }
  }, [extractionError, setError, goToPhase]);

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  // File upload
  const handleFileUpload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      // Reset any previous state
      if (extractionError || extractedData) {
        resetExtraction();
        reset();
        hasInitialized.current = false;
      }

      await extractBooking(files[0]);
    },
    [extractBooking, extractionError, extractedData, resetExtraction, reset]
  );

  // Use email forwarding instead of file upload
  // Uses pending token - case will be created when email arrives
  const handleUseEmailForwarding = useCallback(() => {
    if (!state.inboundEmailAddress) {
      toast.error('Email forwarding is not ready yet. Please wait a moment and try again.');
      return;
    }

    if (state.tokenExpired) {
      toast.error('Session expired. Please reload the page to get a new email address.');
      return;
    }

    // If we already have a case, connect to WebSocket for notifications
    if (state.caseId) {
      emailNotification.connect();
    }
    // Note: If only pending token exists, case will be created when email arrives
    // The user will need to return to this page to continue

    // Transition to awaiting_email phase
    goToPhase('awaiting_email');

    toast.info('Forward your booking confirmation to the email address shown');
  }, [state.inboundEmailAddress, state.tokenExpired, state.caseId, emailNotification, goToPhase]);

  // Re-upload (from booking review)
  const handleReupload = useCallback(() => {
    resetExtraction();
    reset();
    hasInitialized.current = false;
    goToPhase('welcome');
  }, [resetExtraction, reset, goToPhase, hasInitialized]);

  // Start fresh - discard current draft and start a new claim
  const handleStartFresh = useCallback(async () => {
    // If we have a case, delete it from the database
    if (state.caseId) {
      try {
        await api.delete(`/api/cases/${state.caseId}/`);
        toast.info('Draft deleted');
      } catch (err) {
        console.error('Failed to delete case:', err);
        // Continue anyway - user wants to start fresh
      }
    }

    // Clear localStorage draft
    clearDraftFromStorage();

    // Reset all state (including pending token)
    resetExtraction();
    reset();

    // Reset refs
    hasInitialized.current = false;
    hasLoadedDraft.current = false;
    isCreatingCase.current = false;

    // Go to welcome - a new pending token will be fetched automatically
    goToPhase('welcome');
  }, [state.caseId, resetExtraction, reset, goToPhase, hasInitialized, hasLoadedDraft, isCreatingCase]);

  // Continue to next phase
  const handleContinue = useCallback(() => {
    goNext();
  }, [goNext]);

  // Go back
  const handleBack = useCallback(() => {
    goBack();
  }, [goBack]);

  // Skip (for optional phases)
  const handleSkip = useCallback(() => {
    goNext();
  }, [goNext]);

  // Incident type selection
  const handleSelectIncidentType = useCallback(
    (type: IncidentType) => {
      setIncidentType(type);
      // Auto-select first segment if only one
      if (visibleSegments.length === 1 && !state.affectedSegmentId) {
        setAffectedSegment(visibleSegments[0]._id);
      }
    },
    [setIncidentType, visibleSegments, state.affectedSegmentId, setAffectedSegment]
  );

  // Upload document
  const handleUploadDocument = useCallback(
    async (file: File, forRequirement?: string) => {
      const docId = `doc_${Date.now()}`;
      const newDoc: UploadedDocument = {
        id: docId,
        name: file.name,
        type: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        isUploading: true,
        // Track which requirement this upload is for (user assignment)
        fulfillsRequirement: forRequirement,
      };

      addDocument(newDoc);

      try {
        // If we have a case ID, upload to backend
        if (state.caseId) {
          const formData = new FormData();
          formData.append('file', file);
          // Include fulfills_requirement in the upload
          if (forRequirement) {
            formData.append('fulfills_requirement', forRequirement);
          }

          const response = await api.post(
            apiEndpoints.uploadDocument(state.caseId),
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );

          updateDocument(docId, {
            isUploading: false,
            storageUrl: response.data.storage_url,
            category: response.data.kind,
            aiConfidence: response.data.ai_confidence,
            // Keep the user-assigned requirement (takes priority over AI)
            fulfillsRequirement: forRequirement || response.data.fulfills_requirement,
          });
        } else {
          // Just mark as uploaded locally
          updateDocument(docId, { isUploading: false });
        }
      } catch (err) {
        console.error('Failed to upload document:', err);
        updateDocument(docId, {
          isUploading: false,
          error: 'Upload failed',
        });
        toast.error('Failed to upload document');
      }
    },
    [state.caseId, addDocument, updateDocument]
  );

  // Add expense
  const handleAddExpense = useCallback(
    (expense: Omit<Expense, 'id' | '_isNew'>) => {
      const newExpense: Expense = {
        ...expense,
        id: `exp_${Date.now()}`,
        _isNew: true,
      };
      addExpense(newExpense);
    },
    [addExpense]
  );

  // Submit claim
  const handleSubmit = useCallback(async () => {
    goToPhase('submitting');

    try {
      // Step 1: Create case if not exists
      let caseId = state.caseId;
      let caseNumber = state.caseNumber;

      if (!caseId) {
        const createResponse = await api.post(apiEndpoints.createCase, {
          incident_type: state.incidentType,
        });

        // API returns { status: 'success', case: { id, case_number, ... } }
        if (!createResponse.data.case?.id) {
          throw new Error('Failed to create case: No case ID returned');
        }

        caseId = String(createResponse.data.case.id);
        caseNumber = createResponse.data.case.case_number;
        setCaseId(caseId, caseNumber);
      }

      // Step 2: Apply booking extraction (create passengers + segments)
      const extractionPayload = {
        pnr: state.pnr,
        passengers: state.passengers.map((p) => ({
          first_name: p.first_name,
          last_name: p.last_name,
        })),
        flight_segments: visibleSegments.map((seg) => ({
          carrier: seg.carrier,
          flight_no: seg.flight_no,
          operating_carrier: seg.operating_carrier,
          dep_iata: seg.dep_iata,
          arr_iata: seg.arr_iata,
          dep_date: seg.dep_date,
          dep_time: seg.dep_time,
          arr_date: seg.arr_date,
          arr_time: seg.arr_time,
          booking_class_category: seg.booking_class_category,
        })),
      };

      await api.post(apiEndpoints.applyExtraction(caseId), extractionPayload);

      // Step 3: Update case with incident info
      await api.put(apiEndpoints.updateCase(caseId), {
        incident_type: state.incidentType,
      });

      // Step 4: Save expenses if any
      for (const expense of state.expenses) {
        await api.post(apiEndpoints.expenses(caseId), {
          expense_type: expense.expense_type,
          amount_cents: expense.amount_cents,
          currency: expense.currency,
          description: expense.description,
        });
      }

      // Step 5: Save narrative if any
      if (state.narrative.trim()) {
        await api.put(apiEndpoints.narrative(caseId), {
          narrative: state.narrative,
          skipped: false,
        });
      }

      // Step 6: Complete intake
      await api.post(apiEndpoints.complete(caseId));

      // Clear draft
      clearDraftFromStorage();
      markSaved();

      // Success!
      goToPhase('complete');
    } catch (err: any) {
      console.error('Failed to submit claim:', err);
      setError(err.message || 'Failed to submit claim');
      goToPhase('review');
      toast.error('Failed to submit claim. Please try again.');
    }
  }, [
    state,
    visibleSegments,
    setCaseId,
    markSaved,
    setError,
    goToPhase,
  ]);

  // View case (after completion)
  const handleViewCase = useCallback(() => {
    if (state.caseId) {
      navigate({ to: `/client/case/${state.caseId}` });
    } else {
      navigate({ to: '/client' });
    }
  }, [state.caseId, navigate]);

  // Back to overview
  const handleBackToOverview = useCallback(() => {
    navigate({ to: '/client' });
  }, [navigate]);

  // Edit section from review
  const handleEditSection = useCallback(
    (section: string) => {
      switch (section) {
        case 'booking':
          goToPhase('booking_review');
          break;
        case 'incident':
          goToPhase('incident_select');
          break;
        case 'documentation':
          goToPhase('documentation');
          break;
        case 'expenses':
          goToPhase('expenses');
          break;
        case 'narrative':
          goToPhase('narrative');
          break;
      }
    },
    [goToPhase]
  );

  // Segment update handler
  const handleUpdateSegment = useCallback(
    (id: string, field: keyof FlightSegment, value: string) => {
      updateSegment(id, field, value);
    },
    [updateSegment]
  );

  // Select affected segment
  const handleSelectAffectedSegment = useCallback(
    (id: string) => {
      setAffectedSegment(id);
    },
    [setAffectedSegment]
  );

  // Show global banner for reconnecting/error states (not in complete phase)
  const showConnectionBanner =
    (wsStatus === 'reconnecting' || wsStatus === 'error') &&
    state.phase !== 'complete';

  return (
    <div className="relative min-h-screen">
      {/* Global connection status banner */}
      <AnimatePresence>
        {showConnectionBanner && (
          <ConnectionStatusBanner
            wsStatus={wsStatus}
            reconnectAttempt={reconnectAttempt}
            onRetry={retryConnection}
          />
        )}
      </AnimatePresence>

      <Canvas
        state={state}
        theme={theme}
        wsStatus={wsStatus}
        reconnectAttempt={reconnectAttempt}
        onRetryConnection={retryConnection}
        extractionProgress={extractionProgress}
        // Email notification state
        emailNotificationState={{
          emailReceived: emailNotification.emailReceived,
          emailSubject: emailNotification.emailSubject,
          emailFrom: emailNotification.emailFrom,
          isExtracting: emailNotification.isExtracting,
          error: emailNotification.error,
          errorDetails: emailNotification.errorDetails,
        }}
        // Segment editing
        onUpdateSegment={handleUpdateSegment}
        onDeleteSegment={deleteSegment}
        onAddSegment={addSegment}
        // Passenger editing
        onUpdatePassenger={updatePassenger}
        onDeletePassenger={deletePassenger}
        onAddPassenger={addPassenger}
        // Navigation
        onContinue={handleContinue}
        onBack={handleBack}
        onSkip={handleSkip}
        onReupload={handleReupload}
        // File upload
        onFileUpload={handleFileUpload}
        // Email forwarding
        onUseEmailForwarding={handleUseEmailForwarding}
        // Incident
        onSelectIncidentType={handleSelectIncidentType}
        onDescriptionChange={setIncidentDescription}
        onSelectAffectedSegment={handleSelectAffectedSegment}
        // Documents
        onUploadDocument={handleUploadDocument}
        onRemoveDocument={removeDocument}
        // Expenses
        onAddExpense={handleAddExpense}
        onRemoveExpense={removeExpense}
        // Narrative
        onNarrativeChange={setNarrative}
        // Submit & actions
        onSubmit={handleSubmit}
        onEditSection={handleEditSection}
        onViewCase={handleViewCase}
        onBackToOverview={handleBackToOverview}
        onStartFresh={handleStartFresh}
      />

      {/* Theme toggle */}
      {onThemeChange && (
        <AuthThemeToggle currentTheme={theme} onToggle={onThemeChange} />
      )}
    </div>
  );
}
